import { SingleSelectDropdown,SingleSelectDropdownProps } from '@tableau/widgets';
import { FormikContextType, useField, useFormikContext } from 'formik';
import React from 'react';
import ReactDOM = require('react-dom');

export type FormikSingleSelectDropdownProps = Omit<SingleSelectDropdownProps, 'onChange'> & {
  name: string;
  /**
   * Optional callback invoked with the newly selected value after the Formik field has been updated.
   * Use this for side effects on change (e.g. showing a toast) without overriding the default Formik wiring.
   */
  onValueChange?: (value: string) => void;
};

/**
 * A wrapper around {@link SingleSelectDropdown} which auto-wires to a {@link Formik} field using the `name`
 * prop. The props are identical to {@link SingleSelectDropdown}, except that the change
 * handler are calculated automatically. It is still possible to supply them in props to override the default behavior.
 */
export function FormikSingleSelectDropdown(props: FormikSingleSelectDropdownProps) {
  const [field, meta, helpers] = useField(props.name);
  const form: FormikContextType<any> = useFormikContext();

  const changeHandler = React.useCallback(
    (value: string) => {
      helpers.setValue(value);
      props.onValueChange?.(value);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [helpers.setValue, props.onValueChange]
  );

  const handleButtonRef = (ref: HTMLButtonElement | null) => {
    let buttonEl = ref ? ReactDOM.findDOMNode(ref) as HTMLElement : undefined;
    if (buttonEl) {
      buttonEl.setAttribute('name', props.name);
      buttonEl.setAttribute('type', 'button');
    }
  };

  // onValueChange is consumed here and must not be forwarded to the underlying widget.
  const { onValueChange, ...dropdownProps } = props;

  return (
    <SingleSelectDropdown list={props.list}
                          onChange={changeHandler}
                          value={field.value}
                          disabled={form.isSubmitting}
                          buttonRef={handleButtonRef}
                          {...dropdownProps}
    />
  );
}
